/**
 * Ein ZIP-Archiv schreiben — eigener Quelltext, ohne Bibliothek.
 *
 * ⚠️ **Warum selbst geschrieben.** Dieselbe Linie wie beim QR-Scanner: eine fremde Bibliothek
 * im Bündel scheidet aus (Prämisse des Nutzers). Ein ZIP ohne Kompression ist klein genug, um
 * es ganz zu verstehen — drei Satzarten und eine Prüfsumme.
 *
 * ⚠️ **Ohne Kompression (`stored`), und das ist kein Nachlassen.** Der Bestand besteht aus
 * JPEG, PNG und mp4; die sind bereits gepackt. Ein zweiter Durchgang kostete Zeit und Speicher
 * und brächte einstellige Prozente. Die Beglaubigungen (`.sig`) und die Ebenen (`.json`) sind
 * daneben winzig.
 *
 * ⚠️ **Die Stücke kommen als Feld heraus, nicht als ein Block.** Ein Prüfungstag bringt
 * schnell hunderte Megabyte Video zusammen; ein einziges `Uint8Array` müsste die Summe am
 * Stück im Speicher halten. Aus den Stücken baut der Aufrufer ein `Blob`, und das legt der
 * Browser bei Bedarf auf die Platte.
 *
 * ⛔ **Kein Zip64.** Über 4 GB oder über 65 535 Dateien wird geworfen, statt ein Archiv zu
 * schreiben, das kein Entpacker lesen kann. Ein stiller Fehler wäre hier der schlimmste:
 * bemerkt würde er erst, wenn jemand die Belege wirklich braucht.
 */

const LOKAL = 0x04034b50;
const VERZEICHNIS = 0x02014b50;
const ENDE = 0x06054b50;

/** Dateinamen sind UTF-8 — das sagt Bit 11 der Kennzeichen (`language encoding flag`). */
const UTF8_FLAG = 0x0800;

/** Version, die zum Entpacken nötig ist: 2.0 genügt für `stored` und Deflate. */
const VERSION = 20;

/** Kompressionsmethoden: 0 unverändert, 8 Deflate — beide in Fassung 2.0 enthalten. */
const GESPEICHERT = 0;
const DEFLATE = 8;

/**
 * „Von wem erzeugt" — **oberes Byte 3 (Unix)**, unteres 20 (Fassung 2.0).
 *
 * ⚠️ **Das ist nicht Zierde, daran hängt der Dateiname.** Steht hier die Herkunft 0
 * (MS-DOS/FAT), liest Info-ZIP — die Fassung, die macOS mitbringt — den Namen als CP437 und
 * **ignoriert das UTF-8-Kennzeichen in Bit 11**. Eine Datei `logs/Prüfling-Müller/größe.txt`
 * ist dann unter ihrem eigenen Namen nicht mehr zu finden: `unzip -p` antwortet
 * „filename not matched".
 *
 * Am 2026-09-22 Byte für Byte nachgestellt: unser Archiv und eines aus Pythons `zipfile`
 * unterschieden sich in **genau diesem einen Feld** (20 gegen 788), lokale Kopfsätze und
 * Namensbytes waren gleich — und Pythons Archiv liess sich öffnen, unseres nicht.
 */
const ERZEUGT_VON = (3 << 8) | VERSION;

export const GRENZE_BYTES = 0xffffffff;
export const GRENZE_EINTRAEGE = 0xffff;

/* ------------------------------------------------------------------- CRC-32 */

/**
 * Die Tabelle wird einmal gerechnet, nicht abgeschrieben.
 *
 * ⚠️ Das Polynom `0xedb88320` ist die gespiegelte Form von `0x04c11db7` — dieselbe Prüfsumme,
 * die PNG und gzip verwenden. Eine falsche Spiegelung ergäbe Werte, die für sich stimmig
 * aussehen und die jeder Entpacker ablehnt.
 */
const TABELLE = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[i] = c >>> 0;
  }
  return t;
})();

export function crc32(daten: Uint8Array): number {
  let c = 0xffffffff;
  for (const b of daten) c = TABELLE[(c ^ b) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/* ------------------------------------------------------------------- Zeit */

/**
 * Zeitpunkt im MS-DOS-Format, wie ZIP es seit 1989 führt.
 *
 * ⚠️ **Zwei Sekunden Auflösung und kein Jahr vor 1980** — das ist das Format, nicht unsere
 * Nachlässigkeit. Wer die genaue Zeit braucht, liest sie im Dateinamen oder in der
 * Beglaubigung; beide tragen sie auf die Millisekunde.
 */
export function dosZeit(d: Date): { zeit: number; datum: number } {
  const jahr = Math.max(d.getFullYear(), 1980);
  return {
    zeit: (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1),
    datum: ((jahr - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate(),
  };
}

/* ------------------------------------------------------------------- Der Bauer */

interface Eintrag {
  name: Uint8Array;
  methode: number;
  crc: number;
  groesse: number;
  gepackt: number;
  offset: number;
  zeit: number;
  datum: number;
}

/**
 * Sammelt Stücke und schreibt am Ende das zentrale Verzeichnis.
 *
 * Aufrufreihenfolge: beliebig oft `fuegeHinzu`, dann **einmal** `fertig`.
 */
export class ZipBauer {
  private readonly stuecke: Uint8Array[] = [];
  private readonly eintraege: Eintrag[] = [];
  private offset = 0;
  private abgeschlossen = false;

  /** Wie gross das Archiv bis hierher ist — ohne das Verzeichnis am Ende. */
  get bisher(): number {
    return this.offset;
  }

  fuegeHinzu(name: string, daten: Uint8Array, geaendert: Date = new Date()): void {
    this.schreibe(name, GESPEICHERT, crc32(daten), daten.length, daten, geaendert);
  }

  /**
   * Ein schon gepackter Eintrag: `daten` ist rohes Deflate (RFC 1951), `crc` und `groesse`
   * gehören zum **ungepackten** Inhalt. Gepackt wird am iPad nativ, am Desktop in Node
   * (`platform/dateien.ts::liesGepackt`) — iOS 15 hat kein `CompressionStream`.
   */
  fuegeGepacktHinzu(
    name: string,
    gepackt: { daten: Uint8Array; crc: number; groesse: number },
    geaendert: Date = new Date(),
  ): void {
    this.schreibe(name, DEFLATE, gepackt.crc >>> 0, gepackt.groesse, gepackt.daten, geaendert);
  }

  private schreibe(
    name: string,
    methode: number,
    crc: number,
    groesse: number,
    daten: Uint8Array,
    geaendert: Date,
  ): void {
    if (this.abgeschlossen) throw new Error('Das Archiv ist bereits abgeschlossen.');
    if (this.eintraege.length >= GRENZE_EINTRAEGE) {
      throw new Error('Mehr als ' + String(GRENZE_EINTRAEGE) + ' Dateien passen nicht in ein Archiv ohne Zip64.');
    }

    // ⚠️ Im Archiv steht immer der Schrägstrich, auch wenn das System einen anderen führt.
    const namensBytes = new TextEncoder().encode(name.replace(/\\/g, '/'));
    const { zeit, datum } = dosZeit(geaendert);
    if (groesse > GRENZE_BYTES) throw new Error('Eine Datei über 4 GB bräuchte Zip64.');

    const kopf = new Uint8Array(30 + namensBytes.length);
    const sicht = new DataView(kopf.buffer);
    sicht.setUint32(0, LOKAL, true);
    sicht.setUint16(4, VERSION, true);
    sicht.setUint16(6, UTF8_FLAG, true);
    sicht.setUint16(8, methode, true);
    sicht.setUint16(10, zeit, true);
    sicht.setUint16(12, datum, true);
    sicht.setUint32(14, crc, true);
    sicht.setUint32(18, daten.length, true);
    sicht.setUint32(22, groesse, true);
    sicht.setUint16(26, namensBytes.length, true);
    sicht.setUint16(28, 0, true);
    kopf.set(namensBytes, 30);

    this.eintraege.push({ name: namensBytes, methode, crc, groesse, gepackt: daten.length, offset: this.offset, zeit, datum });
    this.stuecke.push(kopf, daten);
    this.offset += kopf.length + daten.length;
    this.pruefeGrenze();
  }

  fertig(): Uint8Array[] {
    if (this.abgeschlossen) throw new Error('Das Archiv ist bereits abgeschlossen.');
    this.abgeschlossen = true;

    const beginn = this.offset;
    for (const e of this.eintraege) {
      const satz = new Uint8Array(46 + e.name.length);
      const sicht = new DataView(satz.buffer);
      sicht.setUint32(0, VERZEICHNIS, true);
      sicht.setUint16(4, ERZEUGT_VON, true);
      sicht.setUint16(6, VERSION, true);
      sicht.setUint16(8, UTF8_FLAG, true);
      sicht.setUint16(10, e.methode, true);
      sicht.setUint16(12, e.zeit, true);
      sicht.setUint16(14, e.datum, true);
      sicht.setUint32(16, e.crc, true);
      sicht.setUint32(20, e.gepackt, true);
      sicht.setUint32(24, e.groesse, true);
      sicht.setUint16(28, e.name.length, true);
      sicht.setUint16(30, 0, true); // extra
      sicht.setUint16(32, 0, true); // Bemerkung
      sicht.setUint16(34, 0, true); // Datenträger
      sicht.setUint16(36, 0, true); // innere Merkmale
      // Unix-Rechte `-rw-r--r--` im oberen Halbwort: mit Erzeuger Unix (s. `ERZEUGT_VON`) und 0 hier
      // entpackten unzip und Finder die Dateien für niemanden lesbar (Befund relay-14, 2026-10-18).
      sicht.setUint32(38, 0o100644 * 0x10000, true); // äussere Merkmale
      sicht.setUint32(42, e.offset, true);
      satz.set(e.name, 46);
      this.stuecke.push(satz);
      this.offset += satz.length;
    }

    const schluss = new Uint8Array(22);
    const sicht = new DataView(schluss.buffer);
    sicht.setUint32(0, ENDE, true);
    sicht.setUint16(4, 0, true);
    sicht.setUint16(6, 0, true);
    sicht.setUint16(8, this.eintraege.length, true);
    sicht.setUint16(10, this.eintraege.length, true);
    sicht.setUint32(12, this.offset - beginn, true);
    sicht.setUint32(16, beginn, true);
    sicht.setUint16(20, 0, true);
    this.stuecke.push(schluss);
    this.offset += schluss.length;

    this.pruefeGrenze();
    return this.stuecke;
  }

  private pruefeGrenze(): void {
    if (this.offset > GRENZE_BYTES) {
      throw new Error('Das Archiv überschreitet 4 GB; dafür bräuchte es Zip64.');
    }
  }
}

/** Der bequeme Weg für kleine Mengen — für grosse besser `ZipBauer` und ein `Blob`. */
export function schreibeZip(
  eintraege: { name: string; daten: Uint8Array; geaendert?: Date }[],
): Uint8Array[] {
  const bauer = new ZipBauer();
  for (const e of eintraege) bauer.fuegeHinzu(e.name, e.daten, e.geaendert);
  return bauer.fertig();
}
